'use client'

import { Link } from '@/i18n/navigation'
import { useLocale } from 'next-intl'
import { t } from '@/i18n/translations'
import type { Pathnames } from '@/i18n/routing'
import { openCookieSettings } from '@/components/CookieConsent'

const marketLinks: { label?: string; labelKey?: string; href: Pathnames | { pathname: Pathnames; params: Record<string, string> } }[] = [
  { label: 'Nasdaq', href: { pathname: '/market/[id]', params: { id: 'ndx' } } },
  { label: 'Dow Jones', href: { pathname: '/market/[id]', params: { id: 'dji' } } },
  { label: 'DAX', href: { pathname: '/market/[id]', params: { id: 'dax' } } },
  { label: 'FTSE 100', href: { pathname: '/market/[id]', params: { id: 'ftse100' } } },
  { labelKey: 'footer.gold', href: { pathname: '/market/[id]', params: { id: 'gold' } } },
  { labelKey: 'footer.dollarTl', href: { pathname: '/forex/[pair]', params: { pair: 'USDTRY' } } },
]

const exploreKeys: { key: string; href: Pathnames | { pathname: Pathnames; params: Record<string, string> } }[] = [
  { key: 'nav.news', href: '/news' },
  { key: 'nav.guides', href: '/guides' },
  { key: 'nav.crypto', href: { pathname: '/crypto/[symbol]', params: { symbol: 'BTC' } } },
  { key: 'sidebar.forex', href: { pathname: '/forex/[pair]', params: { pair: 'USDTRY' } } },
  { key: 'nav.brokers', href: '/brokers' },
]

const infoKeys: { key: string; href: Pathnames }[] = [
  { key: 'footer.about', href: '/about' },
  { key: 'footer.contact', href: '/contact' },
  { key: 'footer.privacy', href: '/privacy' },
  { key: 'footer.terms', href: '/terms' },
]

function FooterLink({ href, label }: { href: Pathnames | { pathname: Pathnames; params: Record<string, string> }; label: string }) {
  return (
    <Link
      href={href as never}
      title={label}
      className="block text-[13px] py-2.5 no-underline transition-colors duration-200 hover:text-[var(--text-main)]"
      style={{ color: 'var(--text-muted)' }}
    >
      {label}
    </Link>
  )
}

function SectionTitle({ children }: { children: string }) {
  return (
    <p
      className="text-[11px] font-bold uppercase tracking-widest mb-4 m-0"
      style={{ color: 'var(--text-main)' }}
    >
      {children}
    </p>
  )
}

function IconX() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  )
}

function IconLinkedIn() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  )
}

function IconInstagram() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  )
}

export default function Footer() {
  const lang = useLocale()

  return (
    <footer className="border-t mt-12 pb-16 md:pb-0" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-card)' }}>
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="grid grid-cols-3 md:grid-cols-4 gap-8 md:gap-12">
          {/* Brand */}
          <div className="col-span-3 md:col-span-1">
            <Link href="/" title="Borsaya.com" className="inline-block mb-4">
              {/* Vektör logo (P1). Tema seçimi artık CSS ile: eskiden next-themes
                  `isDark` state'ine bakılıyordu, o da hydration'dan SONRA oturduğu
                  için ilk boyamada yanlış logo görünüp değişiyordu.
                  width/height gerçek en-boy oranını taşır — eskiden 120×40 yazıyordu
                  ve hiçbir logo dosyasının oranına uymuyordu (CLS kaynağı). */}
              <img
                src="/logo.svg"
                alt="Borsaya"
                width={228}
                height={300}
                className="h-8 w-auto dark:hidden"
              />
              <img
                src="/logo_dark.svg"
                alt="Borsaya"
                width={713}
                height={492}
                className="h-8 w-auto hidden dark:block"
              />
            </Link>
            <p className="text-[12px] leading-relaxed m-0 mb-5" style={{ color: 'var(--text-muted)' }}>
              {t('footer.desc', lang)}
            </p>
            <div className="flex items-center gap-3">
              {[
                { icon: <IconX />, href: 'https://x.com/borsayacom', label: 'X' },
                { icon: <IconLinkedIn />, href: 'https://www.linkedin.com/in/borsaya-com-38a9973b6/', label: 'LinkedIn' },
                { icon: <IconInstagram />, href: 'https://www.instagram.com/borsayacom', label: 'Instagram' },
              ].map(s => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  title={s.label}
                  className="flex items-center justify-center w-11 h-11 rounded-lg transition-all duration-200 hover:text-[var(--accent-color)] hover:bg-[var(--border-color)]"
                  style={{ color: 'var(--text-muted)', backgroundColor: 'var(--bg-body)' }}
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Markets */}
          <div>
            <SectionTitle>{t('footer.markets', lang)}</SectionTitle>
            {marketLinks.map(l => {
              const label = l.labelKey ? t(l.labelKey, lang) : l.label!
              return <FooterLink key={label} href={l.href as never} label={label} />
            })}
          </div>

          {/* Explore */}
          <div>
            <SectionTitle>{t('footer.explore', lang)}</SectionTitle>
            {exploreKeys.map(l => <FooterLink key={l.key} href={l.href as never} label={t(l.key, lang)} />)}
          </div>

          {/* Info */}
          <div>
            <SectionTitle>{t('footer.info', lang)}</SectionTitle>
            {infoKeys.map(l => <FooterLink key={l.key} href={l.href as never} label={t(l.key, lang)} />)}
            {/* Çerez tercihlerini yeniden açar — rıza, verildiği kadar kolay geri alınabilmeli. */}
            <button
              type="button"
              onClick={openCookieSettings}
              title={t('footer.cookieSettings', lang)}
              className="block w-full text-left text-[13px] py-2.5 bg-transparent border-0 cursor-pointer transition-colors duration-200 hover:text-[var(--text-main)]"
              style={{ color: 'var(--text-muted)' }}
            >
              {t('footer.cookieSettings', lang)}
            </button>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t" style={{ borderColor: 'var(--border-color)' }}>
        <div className="max-w-7xl mx-auto px-4 py-5 flex flex-col items-center gap-4">
          <span className="text-[11px] font-medium" style={{ color: 'var(--text-muted)' }}>
            {t('footer.copyright', lang)}
          </span>
          <p className="text-[10px] text-center leading-relaxed m-0 max-w-3xl" style={{ color: 'var(--text-muted)' }}>
            {t('footer.disclaimer', lang)}
          </p>
        </div>
      </div>
    </footer>
  )
}
